import { useState, useEffect, useCallback, useRef } from "react";
import { collection, query, getDocs } from "firebase/firestore";
import { db } from "@/config/firebase";

interface Banner {
  id: string;
  imageUrl: string;
  link?: string;
  alt: string;
  title?: string;
  order: number;
  active: boolean;
}

export default function BannerCarousel() {
  const [banners, setBanners] = useState<Banner[]>([]);
  const [current, setCurrent] = useState(0);
  const touchStart = useRef<number | null>(null);
  const touchDelta = useRef(0);

  useEffect(() => {
    const q = query(collection(db, "banners"));
    getDocs(q).then((snap) => {
      const items = snap.docs
        .map((doc) => ({ id: doc.id, ...doc.data() } as Banner))
        .filter((b) => b.active)
        .sort((a, b) => a.order - b.order);
      setBanners(items);
    });
  }, []);

  const next = useCallback(() => {
    if (banners.length <= 1) return;
    setCurrent((c) => (c + 1) % banners.length);
  }, [banners.length]);

  const prev = useCallback(() => {
    if (banners.length <= 1) return;
    setCurrent((c) => (c - 1 + banners.length) % banners.length);
  }, [banners.length]);

  // Auto-advance
  useEffect(() => {
    if (banners.length <= 1) return;
    const timer = setInterval(next, 5000);
    return () => clearInterval(timer);
  }, [banners.length, next]);

  // Touch swipe
  const onTouchStart = (e: React.TouchEvent) => {
    const touch = e.touches[0];
    if (!touch) return;
    touchStart.current = touch.clientX;
    touchDelta.current = 0;
  };

  const onTouchMove = (e: React.TouchEvent) => {
    if (touchStart.current === null) return;
    const touch = e.touches[0];
    if (!touch) return;
    touchDelta.current = touch.clientX - touchStart.current;
  };

  const onTouchEnd = () => {
    if (Math.abs(touchDelta.current) > 50) {
      if (touchDelta.current > 0) prev();
      else next();
    }
    touchStart.current = null;
  };

  const banner = banners[current];
  if (!banner) return null;

  const img = (
    <img
      src={banner.imageUrl}
      alt={banner.alt}
      className="h-full w-full object-cover"
    />
  );

  return (
    <div
      className="relative mb-6 w-full overflow-hidden sm:mb-8"
      style={{ aspectRatio: "16/9" }}
      onTouchStart={onTouchStart}
      onTouchMove={onTouchMove}
      onTouchEnd={onTouchEnd}
    >
      {banner.link ? (
        <a href={banner.link} className="block h-full">
          {img}
        </a>
      ) : (
        img
      )}

      {/* Title overlay */}
      {banner.title && (
        <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/60 to-transparent p-4 sm:p-6">
          <h2 className="text-base font-bold text-white sm:text-2xl">
            {banner.title}
          </h2>
        </div>
      )}

      {/* Setas — hidden on mobile, shown on desktop */}
      {banners.length > 1 && (
        <>
          <button
            onClick={prev}
            className="absolute left-2 top-1/2 hidden -translate-y-1/2 rounded-full bg-black/30 p-1.5 text-white backdrop-blur-sm transition-colors hover:bg-black/50 sm:block sm:left-3 sm:p-2"
          >
            <svg className="h-4 w-4 sm:h-5 sm:w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 19.5L8.25 12l7.5-7.5" />
            </svg>
          </button>
          <button
            onClick={next}
            className="absolute right-2 top-1/2 hidden -translate-y-1/2 rounded-full bg-black/30 p-1.5 text-white backdrop-blur-sm transition-colors hover:bg-black/50 sm:block sm:right-3 sm:p-2"
          >
            <svg className="h-4 w-4 sm:h-5 sm:w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M8.25 4.5l7.5 7.5-7.5 7.5" />
            </svg>
          </button>
        </>
      )}

      {/* Dots */}
      {banners.length > 1 && (
        <div className="absolute bottom-2 left-1/2 flex -translate-x-1.5 gap-1.5 sm:bottom-3 sm:gap-2">
          {banners.map((_, i) => (
            <button
              key={i}
              onClick={() => setCurrent(i)}
              className={`h-1.5 w-1.5 rounded-full transition-colors sm:h-2 sm:w-2 ${
                i === current ? "bg-white" : "bg-white/40"
              }`}
            />
          ))}
        </div>
      )}
    </div>
  );
}
